// The match camera's motion (2026-10-30, smooth camera; Devin: "if you are scrolling or pressing keys to pan it
// stutters and requires single taps"). Pure: no DOM and no three, so tests/camera.test.ts plays it through.
// Before, each keydown moved the camera one fixed step, so a held key jumped once, waited out the OS repeat delay
// (about half a second) and then stepped at the repeat rate; each wheel event zoomed in one jump. Now the keys only
// say which way is held and the camera moves every frame (step) with a velocity that ramps up and glides to a stop;
// the wheel sets a zoom goal the camera eases to, toward the point under the cursor. A drag and a touch pinch stay 1:1
// under the finger; letting go of a quick drag glides a little. Every ease is an exponential in dt, so the feel is the
// same at 30, 60 or 144 frames a second. The sim never sees any of it.

/** Tuning. Speeds are in camera distances (cam.dist) a second, so a pan feels the same zoomed in or out. */
export const CAM = {
  minDist: 25, maxDist: 190,
  /** Held keys pan this fast: at 1.3 a 16:9 screen's width of ground goes by in about a second. */
  keySpeed: 1.3,
  /** Time constants (s): a held key reaches 95% of its speed in 3 x keyRise (0.1 s); after the last key lifts the
   *  camera glides to a stop in about 3 x keyFall (0.15 s), keySpeed x keyFall = 0.065 of a distance further. */
  keyRise: 0.033, keyFall: 0.05,
  /** The wheel zoom reaches its goal in about 3 x zoomEase (0.12 s): smooth without a rubbery lag. */
  zoomEase: 0.04,
  /** Log zoom per wheel pixel (a 100 px notch zooms 10.5%, as before); at most wheelMax per event, so an accelerated
   *  or page-sized notch doesn't jump. A line is lineToPx pixels, a page pageToPx. A trackpad pinch (ctrlKey) sends
   *  small deltas and gets pinchBoost (d3-zoom's 10x). */
  wheelPx: 0.001, wheelMax: 0.15, lineToPx: 25, pageToPx: 500, pinchBoost: 10,
  /** A drag let go while moving glides on with glideFall (s); slower than glideMin distances a second, or after the
   *  finger rested glideRest ms, it stops dead. glideWindow (ms) of the drag gives its speed, capped at glideMax. */
  glideFall: 0.12, glideMin: 0.25, glideMax: 3, glideWindow: 80, glideRest: 50,
} as const;

/** The pan keys, by the character typed (as the old handler read them, so a layout's letters keep their meaning). */
const DIRS: Record<string, readonly [number, number]> = {
  w: [0, -1], arrowup: [0, -1], s: [0, 1], arrowdown: [0, 1], a: [-1, 0], arrowleft: [-1, 0], d: [1, 0], arrowright: [1, 0],
};

/** How much of the way an exponential ease with time constant tau covers in dt (1 when tau is 0). */
export const ease = (dt: number, tau: number) => (tau > 0 ? 1 - Math.exp(-dt / tau) : 1);
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export class CameraRig {
  /** Key pan velocity and the drag's release glide (m/s). */
  vx = 0; vz = 0; gx = 0; gz = 0;
  /** The wheel's zoom goal and the ground point it zooms toward (null: none). */
  zoomTo: number | null = null;
  anchor: { x: number; z: number } | null = null;
  /** The distance a Safari trackpad pinch started from (gestureStart). */
  private pinchFrom: number | null = null;
  /** prefers-reduced-motion: no glide after a drag or a key. */
  reduced = false;
  /** Held pan keys by e.code (so a key lifted with another modifier or layout still lets go), each with its way. */
  private held = new Map<string, readonly [number, number]>();
  /** The drag's recent moves (time ms, summed applied move), for its release speed. */
  private trail: { t: number; x: number; z: number }[] = [];

  constructor(public x: number, public z: number, public dist: number, readonly size: number) {}

  get holding() { return this.held.size > 0; }

  /** Keep the camera on the board and in its zoom range. */
  clamp() {
    this.dist = clamp(this.dist, CAM.minDist, CAM.maxDist);
    this.x = clamp(this.x, 0, this.size); this.z = clamp(this.z, 0, this.size);
  }

  /** A key went down (repeats too): true when it is a pan key. */
  keyDown(code: string, key: string) {
    const d = DIRS[key.toLowerCase()];
    if (!d) return false;
    this.held.set(code, d);
    return true;
  }
  keyUp(code: string) { this.held.delete(code); }
  /** Let go of every key (the window lost focus, the tab hid, a menu opened): no key can stick down. */
  clearKeys() { this.held.clear(); }

  /** A wheel event: delta in its own unit (deltaMode 0 px, 1 lines, 2 pages); ctrl is a trackpad pinch. `at` is the
   *  ground point under the cursor, which stays under it while the zoom eases in (null zooms on the middle). */
  wheel(deltaY: number, deltaMode: number, ctrl: boolean, at: { x: number; z: number } | null) {
    const px = deltaY * (deltaMode === 1 ? CAM.lineToPx : deltaMode === 2 ? CAM.pageToPx : 1) * (ctrl ? CAM.pinchBoost : 1);
    const step = clamp(px * CAM.wheelPx, -CAM.wheelMax, CAM.wheelMax);
    this.zoomTo = clamp((this.zoomTo ?? this.dist) * Math.exp(step), CAM.minDist, CAM.maxDist);
    this.anchor = at;
  }

  /** Safari's trackpad pinch (2026-10-30): it sends gesturestart / gesturechange, not ctrl wheel. `scale` is the
   *  spread since the pinch began (1 at its start, 2 twice as wide), so the zoom goal is the distance the pinch started
   *  from over scale, eased and anchored like the wheel. gestureStart on gesturestart. */
  gestureStart() { this.pinchFrom = this.zoomTo ?? this.dist; }
  gesture(scale: number, at: { x: number; z: number } | null) {
    if (!(scale > 0) || !isFinite(scale)) return;
    this.zoomTo = clamp((this.pinchFrom ?? this.dist) / scale, CAM.minDist, CAM.maxDist);
    this.anchor = at;
  }

  /** A touch pinch: 1:1 under the fingers about the ground point `at` (the fingers' midpoint), no easing, and any
   *  wheel goal given up. */
  zoomBy(k: number, at: { x: number; z: number } | null = null) {
    this.zoomTo = null; this.anchor = null; this.trail = [];
    const d = Math.max(CAM.minDist, Math.min(CAM.maxDist, this.dist * k)), s = d / this.dist;
    if (at) { this.x = at.x + (this.x - at.x) * s; this.z = at.z + (this.z - at.z) * s; }
    this.dist = d; this.clamp();
  }

  /** A drag: moves the camera now, 1:1 (dx, dz in metres); t in ms for the release speed. */
  drag(dx: number, dz: number, t: number) {
    this.gx = this.gz = 0;
    const x0 = this.x, z0 = this.z;
    this.x += dx; this.z += dz; this.clamp();
    const last = this.trail[this.trail.length - 1];
    this.trail.push({ t, x: (last?.x ?? 0) + this.x - x0, z: (last?.z ?? 0) + this.z - z0 });
    while (this.trail.length > 2 && this.trail[0].t < t - CAM.glideWindow) this.trail.shift();
  }

  /** A finger or button came down on the board: the board holds still under it (glide, wheel zoom and a let-go
   *  key's glide stop; a key still held keeps panning). */
  grab() {
    this.gx = this.gz = 0; this.trail = []; this.zoomTo = null; this.anchor = null;
    if (!this.holding) this.vx = this.vz = 0;
  }

  /** The drag let go at t (ms): glide on at its speed if it was still moving (never with reduced motion). */
  release(t: number) {
    const tr = this.trail, a = tr[0], b = tr[tr.length - 1];
    this.trail = [];
    if (this.reduced || !a || !b || b.t - a.t < 8 || t - b.t > CAM.glideRest) return;
    let vx = (b.x - a.x) / (b.t - a.t) * 1000, vz = (b.z - a.z) / (b.t - a.t) * 1000;
    const v = Math.hypot(vx, vz), max = CAM.glideMax * this.dist;
    if (v < CAM.glideMin * this.dist) return;
    if (v > max) { vx *= max / v; vz *= max / v; }
    this.gx = vx; this.gz = vz;
  }

  /** Jump the camera (a minimap tap, a group's double tap, a new match): every glide and the zoom anchor stop; with
   *  a distance the zoom goal goes too. A key still held ramps up again from there. */
  place(x: number, z: number, dist?: number) {
    this.x = x; this.z = z; this.vx = this.vz = this.gx = this.gz = 0; this.anchor = null; this.trail = [];
    if (dist !== undefined) { this.dist = dist; this.zoomTo = null; }
    this.clamp();
  }

  /** Everything still: keys let go, no velocity, glide or zoom goal (a menu opened). */
  stop() { this.clearKeys(); this.vx = this.vz = this.gx = this.gz = 0; this.zoomTo = null; this.anchor = null; this.trail = []; this.pinchFrom = null; }

  /** Advance dt seconds. True when the camera moved (the caller places the three camera). */
  step(dt: number) {
    if (!(dt > 0)) return false;
    dt = Math.min(dt, 0.25);
    const x0 = this.x, z0 = this.z, d0 = this.dist;
    // zoom: ease the distance toward the goal in log space; the anchor's ground point keeps its place on screen
    // (scaling the camera about a ground point with its angle fixed leaves that point's pixel where it was)
    if (this.zoomTo !== null) {
      const l = Math.log(this.dist), goal = Math.log(this.zoomTo);
      const next = Math.abs(goal - l) < 1e-3 ? goal : l + (goal - l) * ease(dt, CAM.zoomEase);
      const d = Math.exp(next), s = d / this.dist;
      if (this.anchor) { this.x = this.anchor.x + (this.x - this.anchor.x) * s; this.z = this.anchor.z + (this.z - this.anchor.z) * s; }
      this.dist = d;
      if (next === goal) { this.zoomTo = null; this.anchor = null; }
    }
    // keys: the velocity eases toward the held way (diagonals no faster) and the position follows it exactly
    // (the closed form of v' = (target - v) / tau), so the path is the same at any frame rate
    let hx = 0, hz = 0;
    for (const [x, z] of this.held.values()) { hx += x; hz += z; }
    const n = Math.hypot(hx, hz), speed = n > 0 ? CAM.keySpeed * this.dist / n : 0;
    const tx = hx * speed, tz = hz * speed;
    const tau = n > 0 ? CAM.keyRise : this.reduced ? 0 : CAM.keyFall;
    if (tau === 0) { this.vx = tx; this.vz = tz; this.x += tx * dt; this.z += tz * dt; }
    else {
      const k = ease(dt, tau);
      this.x += tx * dt + (this.vx - tx) * tau * k; this.z += tz * dt + (this.vz - tz) * tau * k;
      this.vx += (tx - this.vx) * k; this.vz += (tz - this.vz) * k;
    }
    // the drag's glide (none once reduced motion is on, even one already under way)
    if (this.reduced) this.gx = this.gz = 0;
    if (this.gx || this.gz) {
      const k = ease(dt, CAM.glideFall);
      this.x += this.gx * CAM.glideFall * k; this.z += this.gz * CAM.glideFall * k;
      this.gx *= 1 - k; this.gz *= 1 - k;
    }
    // stop under a hundredth of a distance a second (a tenth of a pixel a frame), taking the rest of the glide at once
    // (v x tau, a few centimetres) so where it stops doesn't depend on the frame rate; at the board's edge stop pushing
    // into it (no bounce, no stored speed)
    const rest = 0.01 * this.dist;
    if (n === 0 && (this.vx || this.vz) && Math.hypot(this.vx, this.vz) < rest) { this.x += this.vx * CAM.keyFall; this.z += this.vz * CAM.keyFall; this.vx = this.vz = 0; }
    if ((this.gx || this.gz) && Math.hypot(this.gx, this.gz) < rest) { this.x += this.gx * CAM.glideFall; this.z += this.gz * CAM.glideFall; this.gx = this.gz = 0; }
    this.clamp();
    if ((this.x <= 0 && this.vx < 0) || (this.x >= this.size && this.vx > 0)) this.vx = 0;
    if ((this.z <= 0 && this.vz < 0) || (this.z >= this.size && this.vz > 0)) this.vz = 0;
    if ((this.x <= 0 && this.gx < 0) || (this.x >= this.size && this.gx > 0)) this.gx = 0;
    if ((this.z <= 0 && this.gz < 0) || (this.z >= this.size && this.gz > 0)) this.gz = 0;
    return this.x !== x0 || this.z !== z0 || this.dist !== d0;
  }
}
